/**
 * Cliente axios compartilhado.
 *
 *  - Anexa o JWT de localStorage como Bearer.
 *  - Em 401, dispara `auth:expired` (AuthContext escuta e desloga).
 *  - Em outros erros, mantém o objeto Axios original (caller decide
 *    se exibe toast — ver react-hot-toast nas mutations).
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

// Migrado de "envmetrics:token" → "envpulse:token". Usuários existentes
// precisarão relogar uma vez (storage antigo fica órfão e é ignorado).
const TOKEN_KEY = "envpulse:token";

const baseURL = import.meta.env.VITE_API_URL ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
});

// --- Token storage helpers ---------------------------------------------------

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// --- Interceptors ------------------------------------------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Limpa imediatamente pra evitar loops.
      setStoredToken(null);
      window.dispatchEvent(new CustomEvent("auth:expired"));
    }
    return Promise.reject(error);
  },
);

// --- Helpers ----------------------------------------------------------------

/**
 * Extrai mensagem amigável do erro da API.
 * FastAPI usa { detail: "..." } pra erros HTTP.
 */
export function apiErrorMessage(err: unknown, fallback = "Erro inesperado"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: unknown } | undefined;
    if (typeof data?.detail === "string") return data.detail;
    if (Array.isArray(data?.detail)) {
      // Erro de validação Pydantic
      return data.detail
        .map((e: { loc?: unknown[]; msg?: string }) =>
          `${(e.loc ?? []).slice(1).join(".")}: ${e.msg}`,
        )
        .join("; ");
    }
    if (err.message) return err.message;
  }
  return fallback;
}
